/**
 * Login Page
 *
 * Firebase email/password authentication with clean UI.
 * Signup mode uses react-hook-form + Zod validation with confirm password
 * and real-time password requirements.
 */

import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';

import { Password } from '../components/Field';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PasswordRequirements } from '../components/PasswordRequirements';
import { useAuth } from '../hooks/useAuth';
import { clearActiveOrgId } from '../utils/activeOrg';
import {
  AccountLinkFailedError,
  AccountLinkRequiredError,
  EmailNotVerifiedError,
  PROVIDER_LABELS,
} from '../utils/auth-errors';

import type { SignupFormData } from './Login/schemas';
import { signupSchema } from './Login/schemas';

/**
 * Parse Firebase auth errors into user-friendly messages
 * Handles blocking function errors, common auth errors, and Firebase error codes
 */
const parseAuthError = (err: unknown): string => {
  if (!(err instanceof Error)) {
    return 'An error occurred during authentication';
  }

  // AccountLinkRequiredError is handled separately in the component — not as a plain string.
  // This case should not normally be reached, but guard defensively.
  if (err instanceof AccountLinkRequiredError) {
    const label = PROVIDER_LABELS[err.pendingProvider] ?? err.pendingProvider;
    return `Sign in with an existing method to link your ${label} account.`;
  }

  const message = err.message;

  // 1. Check for blocking function error (invitation required)
  // Firebase wraps blocking function rejections as auth/internal-error; the actual
  // message is embedded in the error string in different formats depending on environment.
  if (
    message.includes('BLOCKING_FUNCTION_ERROR_RESPONSE') ||
    message.includes('HTTP Cloud Function returned an error') ||
    (message.includes('auth/internal-error') && message.includes('Sign-up requires'))
  ) {
    // Try to extract the actual error message from the blocking function response
    const match = message.match(/"message":"([^"]+)"/) ?? message.match(/\[([^\]]+)\]/);
    if (match?.[1]) {
      return match[1];
    }
    return 'Sign-up requires an invitation. Please contact hello@compounds.dev to request access.';
  }

  // 2. Map Firebase auth error codes to user-friendly messages
  const errorMap: Record<string, string> = {
    // Sign-in errors
    'auth/invalid-credential':
      'Invalid email or password. Please check your credentials and try again.',
    'auth/invalid-login-credentials':
      'Invalid email or password. Please check your credentials and try again.',
    // Same message as auth/invalid-credential — prevents enumeration of which
    // emails are registered vs which have wrong passwords.
    'auth/user-not-found':
      'Invalid email or password. Please check your credentials and try again.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-password': 'Invalid password. Please try again.',

    // Sign-up errors
    'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
    'auth/weak-password': 'Password should be at least 6 characters long.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',

    // Email/validation errors
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/missing-email': 'Email is required.',

    // Account errors
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/account-exists-with-different-credential':
      'An account already exists with the same email but different sign-in method.',

    // Rate limiting
    'auth/too-many-requests':
      'Too many failed attempts. Please try again later or reset your password.',

    // Network errors
    'auth/network-request-failed': 'Network error. Please check your connection and try again.',
    'auth/timeout': 'Request timed out. Please try again.',

    // Popup/redirect errors
    'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again.',
    'auth/popup-blocked': 'Pop-up was blocked by your browser. Please allow pop-ups and try again.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled. Please try again.',
    'auth/redirect-cancelled-by-user': 'Sign-in was cancelled. Please try again.',

    // Token/session errors
    'auth/expired-action-code': 'This link has expired. Please request a new one.',
    'auth/invalid-action-code': 'This link is invalid. Please request a new one.',
    'auth/user-token-expired': 'Your session has expired. Please sign in again.',
    'auth/requires-recent-login': 'Please sign in again to continue.',

    // Configuration errors
    'auth/unauthorized-domain': 'This domain is not authorized for authentication.',
    'auth/invalid-api-key': 'Invalid API configuration. Please contact support.',
    'auth/app-not-authorized': 'This app is not authorized. Please contact support.',

    // Internal errors — only used as fallback; blocking function errors are handled above
    'auth/internal-error': 'An unexpected error occurred. Please try again.',

    // Verification email send failures (from sendVerificationEmail Cloud Function)
    'verification-email-failed':
      "Your account was created but we couldn't send the verification email. Please try signing in to resend it.",
    'verification-email-rate-limited':
      'Too many verification emails sent. Please wait before trying again.',

    'password-reset-failed':
      "We couldn't send the password reset email. Please try again or contact support.",
    'password-reset-rate-limited': 'Too many reset emails sent. Please wait before trying again.',

    'change-email-failed':
      "We couldn't send the confirmation email to your new address. Please try again.",
    'change-email-rate-limited': 'Too many emails sent. Please wait before trying again.',
  };

  // 3. Check for exact error code match
  for (const [code, friendlyMessage] of Object.entries(errorMap)) {
    if (message.includes(code)) {
      return friendlyMessage;
    }
  }

  // 4. Check for generic Firebase error patterns
  if (message.includes('INVALID_LOGIN_CREDENTIALS') || message.includes('INVALID_CREDENTIAL')) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }

  // 5. Return sanitized original message (remove Firebase prefix)
  return message.replace(/^Firebase:\s*/i, '').replace(/\s*\(auth\/[^)]+\)\s*\.?$/, '');
};

export function Login(): React.JSX.Element {
  const { resetPassword, signIn, signInWithGithub, signInWithGoogle, signUp, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const prefillEmail = searchParams.get('email') ?? '';
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(mode === 'signup');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState(prefillEmail);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [linkPrompt, setLinkPrompt] = useState<AccountLinkRequiredError | null>(null);
  const [linkError, setLinkError] = useState<AccountLinkFailedError | null>(null);
  const justSignedUpRef = useRef(false);

  // react-hook-form for signup mode (Zod validation with confirm password)
  const {
    formState: { errors: signupErrors },
    handleSubmit: handleSignupSubmit,
    register: registerSignup,
    reset: resetSignup,
    watch,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onBlur',
    defaultValues: { email: prefillEmail, password: '', confirmPassword: '' },
  });

  const signupPassword = watch('password', '');

  // Get return URL from query params
  const returnTo = searchParams.get('returnTo');

  // Clear activeOrgId on login page mount (user might be switching accounts)
  useEffect(() => {
    clearActiveOrgId();
  }, []);

  // Redirect to home (or returnTo) if already authenticated.
  // Hold the redirect if there is a linkError — the user needs to acknowledge
  // the failure before being sent to the app.
  // Guard against unverified users: signIn() calls signInWithEmailAndPassword
  // which fires onIdTokenChanged (setting `user`) before it can call
  // firebaseSignOut(). Without this check the effect would navigate an
  // unverified user into the app, then ProtectedLayout would bounce them back
  // to /login — creating an infinite redirect loop.
  useEffect(() => {
    if (user && !justSignedUpRef.current && !linkError) {
      if (!user.emailVerified) {
        return;
      }
      const destination = returnTo || '/';
      void Promise.resolve(navigate(destination));
    }
  }, [user, navigate, returnTo, linkError]);

  const executeAuth = async (authEmail: string, authPassword: string): Promise<void> => {
    setError('');
    setLinkPrompt(null);
    setLinkError(null);
    setLoading(true);

    try {
      if (isSignUp) {
        // Set BEFORE calling signUp — signUp triggers onIdTokenChanged
        // (which briefly sets `user`), then always throws EmailNotVerifiedError.
        // Without this guard, the redirect effect would navigate to /projects
        // before signUp completes and signs out.
        justSignedUpRef.current = true;
        await signUp(authEmail, authPassword);
        void navigate(returnTo || '/onboarding', { replace: true });
      } else {
        await signIn(authEmail, authPassword);
      }
    } catch (err) {
      if (err instanceof EmailNotVerifiedError) {
        // Send users to the dedicated verify-email page so they have a clear,
        // stable landing point. Pass credentials in router state (in-memory
        // only) so the page can offer a resend button without another sign-in.
        justSignedUpRef.current = false;
        void navigate('/verify-email', {
          state: { email: err.email, password: authPassword },
          replace: true,
        });
      } else {
        // Reset the guard so the redirect effect works if the user
        // subsequently signs in (e.g. via a provider link prompt).
        justSignedUpRef.current = false;
        if (err instanceof AccountLinkRequiredError) {
          setLinkPrompt(err);
        } else if (err instanceof AccountLinkFailedError) {
          setLinkError(err);
        } else {
          setError(parseAuthError(err));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign-in form submit (simple, no Zod validation)
  const handleSignInSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void executeAuth(email, password);
  };

  // Signup form submit (via react-hook-form + Zod)
  const onSignupSubmit = (data: SignupFormData): void => {
    // Sync email/password state for resend verification flow
    setEmail(data.email);
    setPassword(data.password);
    void executeAuth(data.email, data.password);
  };

  const handleSocialSignIn = (provider: 'google' | 'github'): void => {
    setError('');
    setLinkPrompt(null);
    setLinkError(null);
    setLoading(true);

    const socialAuth = async (): Promise<void> => {
      try {
        if (provider === 'google') {
          await signInWithGoogle();
        } else {
          await signInWithGithub();
        }
      } catch (err) {
        if (err instanceof AccountLinkRequiredError) {
          setLinkPrompt(err);
        } else if (err instanceof AccountLinkFailedError) {
          setLinkError(err);
        } else {
          setError(parseAuthError(err));
        }
      } finally {
        setLoading(false);
      }
    };

    void socialAuth();
  };

  const handleModeSwitch = (): void => {
    setIsSignUp(!isSignUp);
    setIsForgotPassword(false);
    setError('');
    setLinkPrompt(null);
    // Reset both form states
    setEmail(prefillEmail);
    setPassword('');
    resetSignup({ email: prefillEmail, password: '', confirmPassword: '' });
  };

  const handleForgotPasswordSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError('');
    setLoading(true);
    void (async (): Promise<void> => {
      try {
        await resetPassword(forgotPasswordEmail.trim());
        setForgotPasswordSent(true);
      } catch (err) {
        setError(parseAuthError(err));
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleBackToSignIn = (): void => {
    setIsForgotPassword(false);
    setForgotPasswordSent(false);
    setError('');
    setForgotPasswordEmail(email || prefillEmail);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full space-y-8 p-8 bg-card rounded-lg shadow-lg">
        <div>
          <h2 className="text-center text-3xl font-bold text-foreground">
            {isForgotPassword ? 'Reset Password' : isSignUp ? 'Create Account' : 'Sign In'}
          </h2>
          <p className="mt-2 text-center text-sm">Compounds.dev - AI-Powered Product Management</p>
        </div>

        {/* ── Forgot password view ── */}
        {isForgotPassword &&
          (forgotPasswordSent ? (
            <div className="space-y-4">
              <div className="bg-success-50 dark:bg-success-900/20 border border-success-400 dark:border-success-800 text-success-700 dark:text-success-400 px-4 py-3 rounded text-sm">
                <p className="font-semibold">Check your inbox</p>
                <p className="mt-1">
                  If <span className="font-medium">{forgotPasswordEmail}</span> is registered,
                  we&apos;ve sent a link to set or reset your password.
                </p>
              </div>
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToSignIn}
                  className="text-sm text-primary underline decoration-primary/50 hover:decoration-primary"
                >
                  Back to sign in
                </button>
              </div>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleForgotPasswordSubmit}>
              <p className="text-sm text-foreground/70">
                Enter your email and we&apos;ll send you a link to reset your password.
              </p>
              {error && (
                <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-400 dark:border-danger-800 text-danger-700 dark:text-danger-400 px-4 py-3 rounded text-sm">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-foreground">
                  Email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={forgotPasswordEmail}
                  onChange={e => setForgotPasswordEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-default rounded-md shadow-sm placeholder-text-muted focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <LoadingSpinner /> : 'Send Reset Link'}
                </button>
              </div>
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToSignIn}
                  className="text-sm text-primary underline decoration-primary/50 hover:decoration-primary"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          ))}

        {/* ── Sign-in / Sign-up view ── */}
        {!isForgotPassword && (
          <>
            {linkError && (
              <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-400 dark:border-danger-800 text-danger-700 dark:text-danger-400 px-4 py-3 rounded text-sm space-y-2">
                <p className="font-semibold">Sign-in succeeded, but account linking failed</p>
                <p>
                  You are signed in, but we couldn&apos;t add{' '}
                  <span className="font-medium">{linkError.pendingProviderLabel}</span> as a sign-in
                  method. You can set this up from{' '}
                  <span className="font-medium">Profile Settings</span> after you continue.
                </p>
                <button
                  type="button"
                  onClick={() => setLinkError(null)}
                  className="w-full py-2 px-4 rounded text-sm font-medium bg-danger-600 text-white hover:bg-danger-700 dark:bg-danger-700 dark:hover:bg-danger-600"
                >
                  Continue to app &rarr;
                </button>
              </div>
            )}

            {linkPrompt && (
              <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-400 dark:border-warning-700 text-warning-800 dark:text-warning-300 px-4 py-3 rounded text-sm space-y-1">
                <p className="font-semibold">Sign in to link your accounts</p>
                <p>
                  Sign in with{' '}
                  <span className="font-medium">
                    {linkPrompt.suggestedProviderLabels.join(' or ')}
                  </span>{' '}
                  to continue &mdash; your{' '}
                  <span className="font-medium">{linkPrompt.pendingProviderLabel}</span> sign-in
                  will be linked automatically.
                </p>
                <p className="text-xs opacity-75">
                  If you refresh this page, click &ldquo;Continue with{' '}
                  {linkPrompt.pendingProviderLabel}
                  &rdquo; again to restart.
                </p>
              </div>
            )}

            {error && (
              <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-400 dark:border-danger-800 text-danger-700 dark:text-danger-400 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Social Sign-In Buttons */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => handleSocialSignIn('google')}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-default rounded-md shadow-sm text-sm font-medium text-foreground bg-background hover:bg-card focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              <button
                type="button"
                onClick={() => handleSocialSignIn('github')}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-default rounded-md shadow-sm text-sm font-medium text-foreground bg-background hover:bg-card focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                    clipRule="evenodd"
                  />
                </svg>
                Continue with GitHub
              </button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-default"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card">Or continue with email</span>
              </div>
            </div>

            {isSignUp ? (
              /* Signup form — react-hook-form + Zod validation */
              <form
                className="space-y-6"
                noValidate
                onSubmit={e => void handleSignupSubmit(onSignupSubmit)(e)}
              >
                <div className="flex flex-col gap-5">
                  <div>
                    <label
                      htmlFor="signup-email"
                      className="block text-sm font-medium text-foreground"
                    >
                      Email
                    </label>
                    <input
                      id="signup-email"
                      type="email"
                      autoComplete="email"
                      className="mt-1 block w-full px-3 py-2 border border-default rounded-md shadow-sm placeholder-text-muted focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground"
                      placeholder="you@example.com"
                      {...registerSignup('email')}
                    />
                    {signupErrors.email && (
                      <p className="mt-1 text-xs text-danger">{signupErrors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <Password
                      id="signup-password"
                      label="Password"
                      autoComplete="new-password"
                      isInvalid={!!signupErrors.password}
                      errorMessage={signupErrors.password?.message}
                      {...registerSignup('password')}
                    />
                    <PasswordRequirements password={signupPassword} />
                  </div>

                  <div>
                    <Password
                      id="signup-confirm-password"
                      label="Confirm Password"
                      autoComplete="new-password"
                      isInvalid={!!signupErrors.confirmPassword}
                      errorMessage={signupErrors.confirmPassword?.message}
                      {...registerSignup('confirmPassword')}
                    />
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? <LoadingSpinner /> : 'Create Account'}
                  </button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleModeSwitch}
                    className="text-sm text-primary underline decoration-primary/50 hover:decoration-primary"
                  >
                    Already have an account? Sign in
                  </button>
                </div>
              </form>
            ) : (
              /* Sign-in form — simple, no Zod validation needed */
              <form className="space-y-6" onSubmit={handleSignInSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-default rounded-md shadow-sm placeholder-text-muted focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-foreground">
                      Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-default rounded-md shadow-sm placeholder-text-muted focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground"
                      placeholder="••••••••"
                    />
                    <div className="mt-1 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setForgotPasswordEmail(email);
                          setIsForgotPassword(true);
                          setError('');
                        }}
                        className="text-xs text-primary underline decoration-primary/50 hover:decoration-primary"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? <LoadingSpinner /> : 'Sign In'}
                  </button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleModeSwitch}
                    className="text-sm text-primary underline decoration-primary/50 hover:decoration-primary"
                  >
                    Don&apos;t have an account? Sign up
                  </button>
                </div>
              </form>
            )}
          </>
        )}

        {/* Dev mode indicator */}
        {import.meta.env.DEV && (
          <div className="mt-4 p-3 bg-warning-light border border-warning rounded text-xs">
            <p className="text-warning-emphasis font-semibold">
              Dev Mode - Using Firebase Emulator
            </p>
            <p className="text-warning-emphasis mt-1">
              Create an account - any email/password you add will work in the emulator
            </p>
            <p className="text-warning-emphasis mt-1">
              <a
                href="http://localhost:4100/auth"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-warning-emphasis"
              >
                Create/view users in Emulator UI &rarr;
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
